import yaml from "js-yaml";

export interface NetworkService {
  id: string;
  name: string;
  port: string;
  isJupyter: boolean;
  containerImage?: string;
  hasPortConflict?: boolean;
  hasPortRangeError?: boolean;
  hasInvalidPortError?: boolean;
  hasNameConflict?: boolean;
  hasNameError?: boolean;
  nameError?: string;
  isCTA?: boolean;
  ctaLabel?: string;
  isEnvironmentManaged?: boolean;
  isFrontendOnly?: boolean;
}

export interface FirewallRule {
  id: string;
  ports: string;
  allowedIPs: string;
  hasPortConflict?: boolean;
  hasInvalidPortError?: boolean;
  hasInvalidFormatError?: boolean;
}

export interface NetworkConfiguration {
  services: NetworkService[];
  firewallRules: FirewallRule[];
  configurationError?: string;
}

export const isValidPortNumber = (port: number): boolean =>
  !isNaN(port) && port >= 1 && port <= 65535;

export const isValidPortFormat = (portStr: string): boolean => {
  if (!portStr.includes("-")) {
    return isValidPortNumber(parseInt(portStr.trim()));
  }
  const parts = portStr.split("-").map((part) => part.trim());
  if (parts.length !== 2) return false;
  const start = parseInt(parts[0]);
  const end = parseInt(parts[1]);
  return isValidPortNumber(start) && isValidPortNumber(end) && start < end;
};

const parsePortRange = (portStr: string): [number, number] => {
  if (!portStr.includes("-")) {
    const port = parseInt(portStr.trim());
    return [port, port];
  }
  const [start, end] = portStr.split("-").map((part) => parseInt(part.trim()));
  return [start, end];
};

export const checkNameConflicts = (
  services: NetworkService[],
): NetworkService[] => {
  const checked = services.map((service) => ({
    ...service,
    hasNameConflict: false,
  }));

  for (let index = 0; index < checked.length; index++) {
    if (!checked[index].name) continue;
    for (let next = index + 1; next < checked.length; next++) {
      if (checked[index].name === checked[next].name) {
        checked[index].hasNameConflict = true;
        checked[next].hasNameConflict = true;
      }
    }
  }

  return checked;
};

export const checkPortConflicts = (
  allServices: NetworkService[],
  allFirewallRules: FirewallRule[],
  _currentItemId?: string,
) => {
  let hasConflict = false;
  let hasRangeError = false;
  let hasInvalidPortError = false;
  const services = allServices.map((service) => ({ ...service }));
  const rules = allFirewallRules.map((rule) => ({ ...rule }));

  services.forEach((service) => {
    service.hasPortConflict = false;
    service.hasPortRangeError = false;
    service.hasInvalidPortError = false;
  });
  rules.forEach((rule) => {
    rule.hasPortConflict = false;
    rule.hasInvalidPortError = false;
    rule.hasInvalidFormatError = false;
    if (!isValidPortFormat(rule.ports)) {
      rule.hasInvalidFormatError = true;
      hasInvalidPortError = true;
    }
  });

  if (!hasInvalidPortError) {
    services.forEach((service) => {
      if (!service.port) return;
      const port = parseInt(service.port);
      if (!isValidPortNumber(port)) {
        service.hasInvalidPortError = true;
        hasInvalidPortError = true;
      }
      if (service.port.includes("-")) {
        service.hasPortRangeError = true;
        hasRangeError = true;
      }
    });

    for (let index = 0; index < services.length; index++) {
      if (!services[index].port) continue;
      const port = parseInt(services[index].port);
      for (let next = index + 1; next < services.length; next++) {
        if (services[next].port && parseInt(services[next].port) === port) {
          services[index].hasPortConflict = true;
          services[next].hasPortConflict = true;
          hasConflict = true;
        }
      }
      rules.forEach((rule) => {
        const [start, end] = parsePortRange(rule.ports);
        if (port >= start && port <= end) {
          services[index].hasPortConflict = true;
          rule.hasPortConflict = true;
          hasConflict = true;
        }
      });
    }

    for (let index = 0; index < rules.length; index++) {
      const [start, end] = parsePortRange(rules[index].ports);
      for (let next = index + 1; next < rules.length; next++) {
        const [nextStart, nextEnd] = parsePortRange(rules[next].ports);
        if (!(end < nextStart || nextEnd < start)) {
          rules[index].hasPortConflict = true;
          rules[next].hasPortConflict = true;
          hasConflict = true;
        }
      }
    }
  }

  return {
    services: checkNameConflicts(services),
    firewallRules: rules,
    hasConflict,
    hasRangeError,
    hasInvalidPortError,
  };
};

const getConcretePort = (value: string | number): string | undefined => {
  const normalized = String(value).trim();
  if (!/^\d+$/.test(normalized)) return undefined;

  const port = Number(normalized);
  return isValidPortNumber(port) ? String(port) : undefined;
};

const getConcretePublishedPort = (
  port:
    | number
    | string
    | { published?: string | number; target: string | number },
): string | undefined => {
  if (typeof port === "string") {
    const withoutProtocol = port.replace(/\/[a-z]+$/i, "");
    const segments = withoutProtocol.split(":");
    if (segments.length < 2) return undefined;

    return getConcretePort(segments[segments.length - 2]);
  }

  if (typeof port === "object" && port !== null) {
    return port.published === undefined
      ? undefined
      : getConcretePort(port.published);
  }

  return undefined;
};

export const parseDockerComposeServices = (
  parsedYaml: string,
  installJupyter: boolean,
): NetworkService[] => {
  const composeConfig = yaml.load(parsedYaml) as any;
  const servicesConfig = composeConfig?.services || {};
  const composeServices = Object.entries(servicesConfig).flatMap(
    ([serviceName, service]: [string, any]) => {
      const image = service.image || "";
      return (service.ports || [])
        .map(
          (
            port:
              | number
              | string
              | { published?: string | number; target: string | number },
            portIndex: number,
          ) => {
            const publishedPort = getConcretePublishedPort(port);
            if (!publishedPort) return null;
            return {
              id: `compose:${serviceName}:${publishedPort}:${portIndex}`,
              name: `${serviceName}-${portIndex + 1}`,
              port: publishedPort,
              isJupyter: false,
              containerImage: image,
              isEnvironmentManaged: true,
              hasPortConflict: false,
              hasPortRangeError: false,
              hasInvalidPortError: false,
            } satisfies NetworkService;
          },
        )
        .filter((service): service is NetworkService => service !== null);
    },
  );

  return installJupyter
    ? [
        {
          id: "docker-compose-jupyter-notebook",
          name: "jupyter-notebook",
          port: "8888",
          isJupyter: true,
          isEnvironmentManaged: true,
          isFrontendOnly: true,
        },
        ...composeServices,
      ]
    : composeServices;
};

const findExistingComposeService = (
  service: NetworkService,
  existingServices: NetworkService[],
  usedIndexes: Set<number>,
) => {
  const index = existingServices.findIndex(
    (existing, existingIndex) =>
      !usedIndexes.has(existingIndex) &&
      existing.isEnvironmentManaged &&
      (existing.id === service.id ||
        (existing.name === service.name && existing.port === service.port)),
  );

  if (index === -1) return undefined;

  usedIndexes.add(index);
  return existingServices[index];
};

export const preserveComposeServiceMetadata = (
  composeServices: NetworkService[],
  existingServices: NetworkService[],
): NetworkService[] => {
  const usedIndexes = new Set<number>();

  return composeServices.map((service) => {
    const existingService = findExistingComposeService(
      service,
      existingServices,
      usedIndexes,
    );
    if (!existingService) return service;

    return {
      ...service,
      ...(existingService.isCTA !== undefined
        ? { isCTA: existingService.isCTA }
        : {}),
      ...(existingService.ctaLabel !== undefined
        ? { ctaLabel: existingService.ctaLabel }
        : {}),
    };
  });
};

export const mergeDockerComposeNetworkServices = (
  existingServices: NetworkService[],
  parsedYaml: string,
  installJupyter: boolean,
): NetworkService[] => {
  const composeServices = preserveComposeServiceMetadata(
    parseDockerComposeServices(parsedYaml, installJupyter),
    existingServices,
  );
  const composePorts = new Set(composeServices.map((service) => service.port));

  return [
    ...existingServices.filter(
      (service) =>
        !service.isEnvironmentManaged &&
        !service.isJupyter &&
        !composePorts.has(service.port),
    ),
    ...composeServices,
  ];
};
